/**
 * Shared announcement state — fetches active announcements once and
 * manages per-user dismissed IDs in localStorage.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { useAuth } from "./auth";
import {
  safeLocalStorageGet,
  safeLocalStorageSet,
  safeLocalStorageRemove,
} from "./storage";

function storageKey(userId) {
  return `xring.dismissedAnnouncements.${userId ?? "anon"}`;
}

function getDismissed(userId) {
  // A blocked store (incognito) reads as "nothing dismissed" (#2028).
  const raw = safeLocalStorageGet(storageKey(userId));
  try {
    return new Set(JSON.parse(raw || "[]"));
  } catch {
    // Stored value is malformed JSON — clear the corrupted entry so it
    // doesn't persist across reloads.
    safeLocalStorageRemove(storageKey(userId));
    return new Set();
  }
}

function saveDismissed(userId, set) {
  safeLocalStorageSet(storageKey(userId), JSON.stringify([...set]));
}

export function useAnnouncements() {
  const { user } = useAuth();
  const userId = user?.id;

  const [all, setAll]           = useState([]);
  const [dismissed, setDismissed] = useState(() => getDismissed(userId));

  // Re-load dismissed state when the user changes (e.g. after login)
  useEffect(() => {
    setDismissed(getDismissed(userId));
  }, [userId]);

  useEffect(() => {
    api.getAnnouncements().then(setAll).catch(() => {});
  }, []);

  const dismiss = useCallback((id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveDismissed(userId, next);
      return next;
    });
  }, [userId]);

  const undismissed = all.filter((a) => !dismissed.has(a.id));

  return { all, undismissed, dismissed, dismiss };
}
